import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ArrowRight, Image as ImageIcon } from "lucide-react";
import Seo from "../components/Seo";
import { ShippingNotice } from "../components/ShippingNotice";
import { useCart } from "../lib/cart";
import { money, sized } from "../lib/api";
import { useContent } from "../context/ContentContext";

function CartThumb({ src, alt, fallback }) {
  const [ok, setOk] = useState(Boolean(src));
  useEffect(() => { setOk(Boolean(src)); }, [src]);
  if (!ok) {
    return (
      <div className="w-24 h-28 flex flex-col items-center justify-center border border-[#E4E1DB] bg-[#F0EFEA] text-[#9a9793]" data-testid="cart-thumb-fallback">
        <ImageIcon className="w-6 h-6" strokeWidth={1.5} />
        <span className="text-[0.6rem] mt-1">{fallback}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} onError={() => setOk(false)} className="w-24 h-28 object-cover border border-[#E4E1DB] bg-[#F0EFEA]" />;
}

export default function Cart() {
  const { items, removeItem, updateQty, subtotal } = useCart();
  const { p } = useContent();
  const c = p("cart");
  const navigate = useNavigate();

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 min-h-[60vh]" data-testid="cart-page">
      <Seo title="Cart — CaminoSapiens" description="Your selected works." noindex />
      <p className="overline mb-4">{c.header.eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl mb-12">{c.header.heading}</h1>

      {items.length > 0 && <ShippingNotice className="mb-8" />}

      {items.length === 0 ? (
        <div className="text-[#4A4947]" data-testid="cart-empty">
          <p>{c.empty.text}</p>
          <div className="flex gap-6 mt-6">
            <Link to="/etchings" className="link-underline text-sm">{c.empty.browse_label}</Link>
            <Link to="/custom-etching" className="link-underline text-sm">{c.empty.commission_label}</Link>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {items.map((it) => (
              <div key={it.line_id} className="flex gap-5 border-b border-[#E4E1DB] pb-6" data-testid={`cart-item-${it.line_id}`}>
                <CartThumb src={sized(it.image, 300)} alt={it.title} fallback={c.summary.thumb_fallback} />
                <div className="flex-1">
                  <h3 className="font-display text-xl">{it.title}</h3>
                  {it.kind === "custom" && (
                    <p className="text-xs text-[#4A4947] mt-1">
                      {it.size_label ? `${it.size_label} · ` : ""}{it.additional_prints ? `${it.additional_prints} extra print${it.additional_prints > 1 ? "s" : ""} · ` : ""}from your photograph{it.upload_filename ? ` (${it.upload_filename})` : ""}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center border border-[#E4E1DB]">
                      <button onClick={() => updateQty(it.line_id, it.quantity - 1)} className="px-3 py-1" data-testid={`cart-minus-${it.line_id}`}>−</button>
                      <span className="px-4 py-1 border-x border-[#E4E1DB] text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(it.line_id, it.quantity + 1)} disabled={it.inventory && it.quantity >= it.inventory} className="px-3 py-1 disabled:opacity-30" data-testid={`cart-plus-${it.line_id}`}>+</button>
                    </div>
                    {it.inventory && it.quantity >= it.inventory ? <span className="text-xs text-[#4A4947]" data-testid={`cart-max-${it.line_id}`}>Max available</span> : null}
                    <button onClick={() => removeItem(it.line_id)} className="text-[#4A4947] hover:text-[#B35D4B]" data-testid={`cart-remove-${it.line_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-right font-display text-lg">{money(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>

          <div className="lg:sticky lg:top-28 h-fit border border-[#E4E1DB] p-6 bg-[#F0EFEA]">
            <div className="flex justify-between text-sm text-[#4A4947]"><span>{c.summary.subtotal_label}</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
            <p className="text-xs text-[#9a9793] mt-2">{c.summary.shipping_note}</p>
            <button onClick={() => navigate("/checkout")} data-testid="cart-checkout" className="w-full mt-6 bg-[#1C1B1A] text-[#F9F8F6] py-4 text-sm hover:bg-[#B35D4B] transition-colors flex items-center justify-center gap-2">
              {c.summary.checkout_label} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
